import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

const AREA_COLORS = {
  // Landuse — green family
  forest:            0x2e6b24,
  wood:              0x2e6b24,
  scrub:             0x4a7a34,
  heath:             0x8a7a44,
  tundra:            0x7a8a68,
  grass:             0x5fad40,
  meadow:            0x68b84a,
  village_green:     0x68b84a,
  orchard:           0x5a9040,
  vineyard:          0x7a9040,
  farmland:          0x9ab060,
  farmyard:          0xb4a870,
  allotments:        0x8aaa50,
  park:              0x5ab050,
  garden:            0x62b855,
  recreation_ground: 0x5ab050,
  cemetery:          0x6a9860,
  common:            0x5ab050,
  nature_reserve:    0x3a7a30,
  // Residential / urban
  residential:       0xd8c8c0,
  commercial:        0xd0c4bc,
  industrial:        0xb0b0b8,
  retail:            0xd8c4bc,
  construction:      0xc8b898,
  brownfield:        0xb8a888,
  greenfield:        0xa8b898,
  military:          0x9a9a80,
  // Sand / beach
  beach:             0xd4bb88,
  sand:              0xd8c090,
  bare_rock:         0xb0a898,
  scree:             0xb0a898,
  // Leisure
  pitch:             0x3a8830,
  track:             0xcc8844,
  golf_course:       0x5ab850,
  playground:        0x7aba60,
  sports_centre:     0x4a9440,
  // Fallback
  default:           0x8aaa60,
};

// Area types rendered as animated water
const WATER_TYPES = new Set([
  'water', 'basin', 'reservoir', 'pond', 'wetland',
]);

// ── Water shaders ─────────────────────────────────────────────────────────────

const WATER_VERT = /* glsl */`
  uniform float time;
  varying vec2 vWorldXZ;
  void main() {
    vec3 pos = position;
    vWorldXZ  = pos.xz;
    float wave = sin(pos.x * 0.22 + time * 1.1) * 0.07
               + sin(pos.z * 0.31 + time * 0.85) * 0.06
               + sin((pos.x + pos.z) * 0.17 + time * 1.4) * 0.04;
    pos.y += wave;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
  }
`;

const WATER_FRAG = /* glsl */`
  uniform float time;
  uniform vec3  waterColor;
  uniform vec3  waterHighlight;
  varying vec2  vWorldXZ;
  void main() {
    float w1 = sin(vWorldXZ.x * 0.38 + vWorldXZ.y * 0.28 + time * 1.7) * 0.5 + 0.5;
    float w2 = sin(vWorldXZ.x * 0.55 - vWorldXZ.y * 0.45 + time * 1.1) * 0.5 + 0.5;
    float ripple = w1 * 0.55 + w2 * 0.45;
    vec3  col    = mix(waterColor, waterHighlight, ripple * 0.32);
    gl_FragColor = vec4(col, 0.88);
  }
`;

// ── Public API ────────────────────────────────────────────────────────────────

/**
 * Build merged mesh for all non-water ground areas.
 */
export function buildAreaMesh(areas, proj, elevFn = null) {
  const geos = [];

  for (const area of areas) {
    if (WATER_TYPES.has(area.areaType)) continue; // water handled separately
    const geo = _buildSingleArea(area, proj, elevFn);
    if (geo) geos.push(geo);
  }

  if (geos.length === 0) return new THREE.Group();

  const merged = mergeGeometries(geos, false);
  geos.forEach(g => g.dispose());

  const mat  = new THREE.MeshStandardMaterial({
    vertexColors: true,
    side: THREE.DoubleSide,
    roughness: 0.98,
    metalness: 0.0,
  });
  const mesh = new THREE.Mesh(merged, mat);
  mesh.receiveShadow = true;
  return mesh;
}

/**
 * Build a single animated water mesh for all water areas.
 * Returns null if no water areas found.
 * The returned mesh's material exposes a `uniforms.time` for animation.
 */
export function buildWaterMesh(areas, proj, elevFn = null) {
  const geos = [];

  for (const area of areas) {
    if (!WATER_TYPES.has(area.areaType)) continue;
    const geo = _buildSingleArea(area, proj, elevFn, false); // no vertex colors
    if (geo) geos.push(geo);
  }

  if (geos.length === 0) return null;

  const merged = mergeGeometries(geos, false);
  geos.forEach(g => g.dispose());

  const mat = new THREE.ShaderMaterial({
    uniforms: {
      time:           { value: 0.0 },
      waterColor:     { value: new THREE.Color(0x2060a0) },
      waterHighlight: { value: new THREE.Color(0x4499cc) },
    },
    vertexShader:   WATER_VERT,
    fragmentShader: WATER_FRAG,
    transparent:    true,
    side:           THREE.DoubleSide,
  });

  const mesh = new THREE.Mesh(merged, mat);
  return mesh;
}

// ── Single area ───────────────────────────────────────────────────────────────

function _buildSingleArea(area, proj, elevFn, addColors = true) {
  const ring = [];
  for (const n of area.nodes) {
    if (n.lat === undefined) continue;
    const p = proj.project(n.lat, n.lon);
    if (!isFinite(p.x) || !isFinite(p.z)) continue;
    ring.push(p);
  }
  // Remove closing duplicate
  if (ring.length >= 2) {
    const f = ring[0], l = ring[ring.length - 1];
    if ((f.x - l.x) ** 2 + (f.z - l.z) ** 2 < 0.01) ring.pop();
  }
  if (ring.length < 3) return null;

  const centElev = elevFn ? _centroidElev(ring, elevFn) : 0;
  const Y = centElev + 0.01;

  const shape = new THREE.Shape(ring.map(p => new THREE.Vector2(p.x, -p.z)));
  const geo   = new THREE.ShapeGeometry(shape);
  geo.rotateX(-Math.PI / 2);
  geo.translate(0, Y, 0);

  if (addColors) {
    const hex    = AREA_COLORS[area.areaType] ?? AREA_COLORS.default;
    const color  = new THREE.Color(hex);
    const count  = geo.attributes.position.count;
    const colArr = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      colArr[i * 3]     = color.r;
      colArr[i * 3 + 1] = color.g;
      colArr[i * 3 + 2] = color.b;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(colArr, 3));
  }

  return geo;
}

function _centroidElev(ring, elevFn) {
  let sx = 0, sz = 0;
  for (const p of ring) { sx += p.x; sz += p.z; }
  return elevFn(sx / ring.length, sz / ring.length);
}
